import React, { useCallback, useMemo } from 'react';

import { Box } from '@rbilabs/universal-components';
import delve from 'dlv';

import Link from 'components/link';
import { useGetMaybeCcpaLinkProps } from 'hooks/one-trust/use-get-maybe-ccpa-link-props';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { isExternalLink } from 'utils/is-external-link';
import { routes } from 'utils/routing';

interface SingleNode {
  title: string;
  path: string;
}
interface ArrayNode {
  name: { locale: string };
  pages: [SingleNode];
}
interface IListProps {
  entries: [SingleNode | ArrayNode];
  borderColor?: string;
  testID?: string;
}

const addPathSlash = (path: string): string =>
  isExternalLink(path) || path.startsWith('/') ? path : `/${path}`;

const getPaths = (nodes: [SingleNode | ArrayNode]): any => {
  return nodes.map((node: SingleNode | ArrayNode) => {
    const title = delve(node, 'localeTitle.locale') || delve(node, 'title') || '';
    const path = delve(node, 'localePath.locale.current') || delve(node, 'path.current') || '';
    if ((node as SingleNode).path) {
      return {
        title,
        // Fix relative paths not having a leading slash
        path: addPathSlash(delve(node, 'redirectUrl.locale') || path),
      };
    }
    return {
      title: (node as ArrayNode).name?.locale,
      pages: getPaths((node as ArrayNode).pages),
    };
  });
};

const Section = Box.withConfig<{ borderColor?: string; hasBorder: boolean }>(p => ({
  borderTopWidth: p.hasBorder ? '1' : '0',
  borderTopColor: p.borderColor ?? Styles.color.grey.five,
}));

const SectionGroup = Box.withConfig({
  paddingTop: '4',
  paddingBottom: '4',
});

const SectionItem = Box.withConfig({
  marginTop: '1',
  marginBottom: 1,
});

export default function ListOfLinks({ entries, borderColor }: IListProps) {
  const enableNutritionExplorerLink = useFlag(LaunchDarklyFlag.ENABLE_NUTRITION_EXPLORER);

  const filterMenuPaths = useCallback(
    (entries: any) => {
      return entries.filter((entry: SingleNode | ArrayNode) => {
        if ('pages' in entry) {
          return filterMenuPaths(entry.pages);
        }
        return entry.path?.includes(routes.nutritionExplorer.slice(1))
          ? enableNutritionExplorerLink
          : true;
      });
    },
    [enableNutritionExplorerLink]
  );

  const paths = useMemo(() => filterMenuPaths(getPaths(entries)), [entries, filterMenuPaths]);

  const getMaybeLinkProps = useGetMaybeCcpaLinkProps();

  return (
    <Box>
      {paths.map((entry: any, i: number) => {
        const isSection = Array.isArray(entry.pages);
        const pages = isSection ? entry.pages : [entry];
        return (
          <Section hasBorder={i > 0} borderColor={borderColor} key={`path-group-${i}`}>
            <SectionGroup>
              {pages.map((node: SingleNode) => {
                return (
                  <SectionItem marginTop="2" key={`${node.title}: ${node.path}`}>
                    <Link
                      {...getMaybeLinkProps({
                        to: node.path,
                        isExternal: isExternalLink(node.path),
                        children: node.title,
                      })}
                    />
                  </SectionItem>
                );
              })}
            </SectionGroup>
          </Section>
        );
      })}
    </Box>
  );
}
